import { Box, Menu, MenuItem } from '@mui/material';
import {
  DataGridPro as DataGrid,
  DataGridProProps,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridCallbackDetails,
  GridCellParams,
  GridColDef,
  GridColumnVisibilityModel,
  GridEventListener,
  GridFilterModel,
  GridPaginationModel,
  GridRowEditStopReasons,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridRowParams,
  GridRowSelectionModel,
  GridSlotsComponent,
  GridSlotsComponentsProps,
  GridSortModel,
  MuiEvent,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { useMutation } from '@tanstack/react-query';
import {
  HTMLAttributes,
  JSXElementConstructor,
  MouseEvent,
  ReactNode,
  useState,
} from 'react';
import { resolvePath, useLocation, useNavigate } from 'react-router';

import useAuth from 'portal/src/auth/useAuth';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import {
  useTableDispatchContext,
  useTableStateContext,
} from 'portal/src/providers/TableProviderContext';
import getNonce from 'portal/src/utils/getNonce';
import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';

import CustomDataGridRow from './CustomDataGridRow';
import CustomNoRowsOverlay from './CustomNoRowsOverlay';
import { CustomToolbar, CustomToolbarProps } from './CustomToolbar';

import { clientSideStringFilterOperators } from './Filters/CustomCollectors';
import type {
  ActionColumnProps,
  InRowEditActionColumnProps,
} from './tables/addActionColumnProps';
import addActionColumnProps from './tables/addActionColumnProps';
import ConfirmChangeActiveDialog from './tables/ConfirmChangeActiveDialog';
import ConfirmDeleteDialog from './tables/ConfirmDeleteDialog';

interface ActionsConfig<TRow extends GridRowModel = GridRowModel> {
  cellClassName?: ActionColumnProps<TRow>['cellClassName'];
  extraActions?: ActionColumnProps<TRow>['extraActions'];
  readActionConfig?: ActionColumnProps<TRow>['readActionConfig'];
  inRowEditConfig?: {
    onHasRowsInEditModeChange?: (hasRowsInEditMode: boolean) => void;
    processRowUpdate?: DataGridProProps['processRowUpdate'];
  } & {
    btnConfig: InRowEditActionColumnProps<TRow>['inRowEditConfig'];
  };
  changeActiveConfig?: {
    snackbarMsgKey: keyof typeof MESSAGES;
    targetStr?: string;
    isDisabledForRow?: (row: TRow) => boolean;
    onChangeActiveStatus: ActionColumnProps<TRow>['onChangeActiveStatus'];
  };
  deleteConfig?: {
    snackbarMsgKey: keyof typeof MESSAGES;
    targetStr?: string;
    message?:
      | string
      | JSX.Element
      | ((row: TRow | undefined) => string | JSX.Element);
    isDisabledForRow?: (row: TRow) => boolean;
    onDeleteClick: ActionColumnProps<TRow>['onDeleteClick'];
  };
}

interface DataTableProps<TRow extends GridRowModel = GridRowModel> {
  actionsConfig?: ActionsConfig<TRow>;
  gridApiRef?: any;
  autoHeight?: boolean;
  checkboxSelection?: boolean;
  columns: GridColDef<TRow>[];
  contextName?: string;
  columnVisibilityModel?: GridColumnVisibilityModel;
  dataFetchError?: boolean;
  defaultGroupingExpansionDepth?: number;
  disableColumnFilter?: boolean;
  disableMultipleColumnsFiltering?: boolean;
  disableMultipleRowSelection?: boolean;
  disableRowSelectionOnClick?: boolean;
  dynamicRowHeight?: boolean;
  filterMode?: 'client' | 'server';
  filterModel?: GridFilterModel;
  hideFooter?: boolean;
  initialState?: any;
  isCellEditable?: (params: GridCellParams) => boolean;
  isRowSelectable?: (e) => boolean;
  isRowNavigation?: boolean;
  loading?: boolean;
  getDetailPanelContent?: (params: GridRowParams<TRow>) => ReactNode;
  getDetailPanelHeight?: (params: GridRowParams<TRow>) => number | 'auto';
  getRowClassName?: (e) => string;
  getRowNavigationPath?: (row: TRow) => string | undefined;
  getTreeDataPath?: DataGridProProps['getTreeDataPath'];
  getRowId?: (e: TRow) => string;
  groupingColDef?: Partial<GridColDef>;
  noDataDisplay?: string | JSX.Element;
  onColumnVisibilityModelChange?: (e) => any;
  onFilterModelChange?: (
    model: GridFilterModel,
    details: GridCallbackDetails<'filter'>,
  ) => void;
  onPaginationModelChange?: (
    model: GridPaginationModel,
    details: GridCallbackDetails<any>,
  ) => void;
  onRowClick?: (params: GridRowParams<TRow>, e: MuiEvent) => void;
  onRowContextMenu?: (row: TRow, event: MouseEvent<HTMLDivElement>) => void;
  onRowDoubleClick?: (e: GridRowParams<TRow>) => void;
  onRowOrderChange?: any;
  onRowSelectionModelChange?: (
    model: GridRowSelectionModel,
    details?: GridCallbackDetails<any>,
  ) => void;
  onSortModelChange?: (
    model: GridSortModel,
    details: GridCallbackDetails<any>,
  ) => void;
  paginationMode?: 'client' | 'server';
  paginationModel?: GridPaginationModel;
  pinCheckboxSelection?: boolean;
  pinActions?: boolean;
  rows: TRow[];
  rowCount?: number;
  rowReordering?: boolean;
  rowSelectionModel?: GridRowSelectionModel;
  slots?: Partial<GridSlotsComponent>;
  slotProps?: GridSlotsComponentsProps;
  sortModel?: GridSortModel;
  sortingMode?: 'client' | 'server';
  sx?: { [key: string]: any };
  toolbarSlotProps?: CustomToolbarProps;
  treeData?: boolean;
}

export default function DataTable<TRow extends GridRowModel>({
  actionsConfig,
  gridApiRef,
  autoHeight = false,
  checkboxSelection = true,
  columns,
  contextName = '',
  columnVisibilityModel,
  dataFetchError = false,
  defaultGroupingExpansionDepth = 0,
  disableColumnFilter = false,
  disableMultipleColumnsFiltering = false,
  disableMultipleRowSelection = false,
  disableRowSelectionOnClick = false,
  dynamicRowHeight = false,
  filterMode = 'client',
  filterModel,
  getDetailPanelContent,
  getDetailPanelHeight,
  getRowClassName,
  getRowNavigationPath,
  getTreeDataPath,
  getRowId,
  groupingColDef,
  hideFooter = false,
  initialState = {},
  isCellEditable,
  isRowNavigation = true,
  isRowSelectable,
  loading = false,
  noDataDisplay,
  onColumnVisibilityModelChange,
  onFilterModelChange,
  onPaginationModelChange,
  onRowClick = null,
  onRowContextMenu,
  onRowDoubleClick,
  onRowOrderChange,
  onRowSelectionModelChange,
  onSortModelChange,
  paginationMode = 'client',
  paginationModel,
  pinCheckboxSelection = true,
  pinActions = true,
  rows,
  rowCount,
  rowReordering,
  rowSelectionModel,
  slots,
  slotProps,
  sortModel,
  sortingMode = 'client',
  sx = {},
  toolbarSlotProps,
  treeData = false,
}: DataTableProps<TRow>) {
  const navigate = useNavigate();
  const location = useLocation();
  const tableStateCtx = useTableStateContext() ?? {};
  const tableDispatchCtx = useTableDispatchContext();
  const snackbarHandler = useSnackbarHandler();
  const { hasReadOnlyAccess } = useAuth();
  const isReadOnly = hasReadOnlyAccess();

  const internalApiRef = useGridApiRef();
  const hasInRowEditConfig = !!actionsConfig?.inRowEditConfig;
  const resolvedApiRef = gridApiRef ?? internalApiRef;

  const [rowIdToDelete, setRowIdToDelete] = useState('');
  const [internalRowModesModel, setInternalRowModesModel] =
    useState<GridRowModesModel>({});
  const [changeActiveStatusConfig, setChangeActiveStatusConfig] = useState<{
    id: string;
    isActive: boolean;
  }>(undefined);
  const [rowContextMenuConfig, setRowContextMenuConfig] = useState<{
    mouseX: number;
    mouseY: number;
    rowPath: string;
  }>(undefined);

  const showToolbar =
    !!toolbarSlotProps?.children ||
    !!toolbarSlotProps?.exportFileName ||
    !!toolbarSlotProps?.leftSideChildren ||
    !!toolbarSlotProps?.onNewButtonClick ||
    !!toolbarSlotProps?.showColumns ||
    !!toolbarSlotProps?.showFilters;

  const {
    columns: defaultColumns,
    filter: defaultFilter,
    sort: defaultSort,
  } = tableStateCtx[contextName] ?? {};
  const showDeactivatedColumn = toolbarSlotProps?.deactivateConfig?.isChecked;
  const resolvedColumnVisibilityModel = {
    ...(defaultColumns ?? columnVisibilityModel ?? {}),
    ...(toolbarSlotProps?.deactivateConfig
      ? { isActive: showDeactivatedColumn }
      : {}),
  };

  const initialStateProps = { ...initialState };
  const shouldUseServerRowCount = paginationMode === 'server';
  const resolvedRowCount = shouldUseServerRowCount
    ? (rowCount ?? 0)
    : undefined;

  const handleInRowModesModelChange = (
    model: GridRowModesModel,
    _details: GridCallbackDetails<any>,
  ) => {
    setInternalRowModesModel(model);
    actionsConfig?.inRowEditConfig?.onHasRowsInEditModeChange?.(
      Object.keys(model).length > 0,
    );
  };

  const resolvedColumns =
    filterMode === 'client'
      ? columns.map((column) => {
          const isStringColumn = !column.type || column.type === 'string';

          if (!isStringColumn || column.filterOperators) {
            return column;
          }

          return {
            ...column,
            filterOperators: clientSideStringFilterOperators,
          };
        })
      : columns;

  if (pinCheckboxSelection || pinActions) {
    initialStateProps.pinnedColumns = {
      ...(initialStateProps?.pinnedColumns ?? {}),
    };

    if (pinCheckboxSelection) {
      initialStateProps.pinnedColumns.left = [
        ...(initialState.pinnedColumns?.left ?? []),
        GRID_CHECKBOX_SELECTION_COL_DEF.field,
      ];
    }
    if (pinActions) {
      initialStateProps.pinnedColumns.right = [
        ...(initialState.pinnedColumns?.right ?? []),
        'actions',
      ];
    }
  }

  // For disabling virtualization in tests and rendering all columns
  const isTestEnvironment = Boolean(import.meta.env.VITE_IS_TEST_ENV);

  const navigateOnRowDoubleClick = ({ id }: GridRowParams) => {
    navigate(id as string);
  };

  const handleRowNavigation = onRowDoubleClick || navigateOnRowDoubleClick;

  const handleStateContextChange = (
    model,
    gridModel: 'columns' | 'filter' | 'sort',
  ) => {
    if (contextName) {
      tableDispatchCtx({
        [contextName]: { ...tableStateCtx[contextName], [gridModel]: model },
      });
    }
  };

  const { mutate: changeActiveStatus, isPending: isChangingActiveStatus } =
    useMutation({
      mutationFn: async () => {
        const { id, isActive } = changeActiveStatusConfig;

        await actionsConfig.changeActiveConfig.onChangeActiveStatus(
          id,
          isActive,
        );
      },
      onSuccess: () => {
        setChangeActiveStatusConfig(undefined);
        snackbarHandler.success(
          MESSAGES[actionsConfig.changeActiveConfig.snackbarMsgKey]?.[
            changeActiveStatusConfig.isActive ? 'DEACTIVATED' : 'REACTIVATED'
          ] ||
            (changeActiveStatusConfig.isActive
              ? 'Row deactivated'
              : 'Row reactivated'),
        );
      },
      onError: () => {
        snackbarHandler.error(MESSAGES.GENERIC.ERROR);
      },
    });

  const { mutate: deleteRow, isPending: isDeleting } = useMutation({
    mutationFn: async () => {
      await actionsConfig.deleteConfig.onDeleteClick(rowIdToDelete);
    },
    onSuccess: () => {
      setRowIdToDelete('');
      snackbarHandler.success(
        MESSAGES[actionsConfig.deleteConfig.snackbarMsgKey]?.['DELETED'] ||
          'Row deleted',
      );
    },
    onError: () => {
      snackbarHandler.error(MESSAGES.GENERIC.ERROR);
    },
  });

  const rowToDelete = actionsConfig?.deleteConfig
    ? rows.find((row, index) => {
        const rowIdentifier =
          getRowId?.(row) ?? (row as { id?: string | number }).id ?? index;

        return String(rowIdentifier) === rowIdToDelete;
      })
    : undefined;

  const deleteMessage = actionsConfig?.deleteConfig?.message
    ? typeof actionsConfig.deleteConfig.message === 'function'
      ? actionsConfig.deleteConfig.message(rowToDelete)
      : actionsConfig.deleteConfig.message
    : undefined;

  const actionColumnProps: ActionColumnProps<TRow> &
    InRowEditActionColumnProps<TRow> = {
    cellClassName: actionsConfig?.cellClassName,
    extraActions: actionsConfig?.extraActions,
    readActionConfig: actionsConfig?.readActionConfig,
    inRowEditConfig: actionsConfig?.inRowEditConfig?.btnConfig,
    changeActiveDisabledForRow:
      actionsConfig?.changeActiveConfig?.isDisabledForRow,
    onChangeActiveStatus: actionsConfig?.changeActiveConfig
      ? (id: string, isActive: boolean) => {
          setChangeActiveStatusConfig({ id, isActive });
        }
      : undefined,
    deleteDisabledForRow: actionsConfig?.deleteConfig?.isDisabledForRow,
    onDeleteClick: actionsConfig?.deleteConfig
      ? (id: string) => setRowIdToDelete(id)
      : undefined,
  };

  const getActiveRowEditIds = () => {
    const internalEditRows = resolvedApiRef?.current?.state?.editRows ?? {};

    return Object.keys(internalEditRows).filter((rowId) => {
      const gridRowMode = internalRowModesModel[rowId];

      return (
        gridRowMode?.mode === GridRowModes.Edit || gridRowMode === undefined
      );
    });
  };

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (
    params,
    event,
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      // Prevents the row from exiting edit mode when clicking outside of the row
      event.defaultMuiPrevented = true;
    }
  };

  const handleIsCellEditable = (params: GridCellParams) => {
    const isCellEditableByConsumer = isCellEditable?.(params) ?? true;

    if (!isCellEditableByConsumer) {
      return false;
    }

    const activeRowEditIds = getActiveRowEditIds();

    if (activeRowEditIds.length === 0) {
      return true;
    }

    return activeRowEditIds.includes(String(params.id));
  };

  const handleRowContextMenu = (
    row: TRow,
    event: MouseEvent<HTMLDivElement>,
  ) => {
    onRowContextMenu?.(row, event);

    const hasReadAction =
      !!actionsConfig?.readActionConfig?.onClick &&
      !(actionsConfig?.readActionConfig?.readHiddenForRow?.(row) ?? false);

    if (event.defaultPrevented || !isRowNavigation || !hasReadAction) {
      return;
    }

    const rowId = getRowId?.(row) ?? row.id;
    const rowPath = getRowNavigationPath?.(row) ?? rowId;

    if (!rowPath) {
      return;
    }

    event.preventDefault();
    setRowContextMenuConfig({
      mouseX: event.clientX,
      mouseY: event.clientY,
      rowPath,
    });
  };

  const handleOpenRowInNewPage = () => {
    if (!rowContextMenuConfig?.rowPath) {
      return;
    }

    const resolvedPath = resolvePath(
      rowContextMenuConfig.rowPath,
      location.pathname,
    );
    const targetUrl = `${window.location.origin}${resolvedPath.pathname}${resolvedPath.search}${resolvedPath.hash}`;

    window.open(targetUrl, '_blank', 'noopener,noreferrer');
    setRowContextMenuConfig(undefined);
  };

  return (
    <Box display="grid" width="100%" height="100%">
      <DataGrid
        apiRef={resolvedApiRef}
        autoHeight={autoHeight}
        autosizeOnMount={isTestEnvironment ? true : undefined}
        checkboxSelection={checkboxSelection}
        columns={
          actionsConfig
            ? [
                ...resolvedColumns,
                addActionColumnProps<TRow>(actionColumnProps),
              ]
            : resolvedColumns
        }
        columnVisibilityModel={resolvedColumnVisibilityModel}
        defaultGroupingExpansionDepth={defaultGroupingExpansionDepth}
        disableColumnFilter={disableColumnFilter}
        disableColumnMenu
        disableMultipleColumnsFiltering={disableMultipleColumnsFiltering}
        disableMultipleRowSelection={disableMultipleRowSelection}
        disableRowSelectionOnClick={disableRowSelectionOnClick}
        disableVirtualization={isTestEnvironment}
        editMode={!isReadOnly && hasInRowEditConfig ? 'row' : undefined}
        filterMode={filterMode}
        filterModel={filterModel ?? defaultFilter}
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={getDetailPanelHeight}
        getRowClassName={getRowClassName}
        getRowHeight={dynamicRowHeight ? () => 'auto' : undefined}
        getRowId={getRowId}
        getTreeDataPath={getTreeDataPath}
        groupingColDef={groupingColDef}
        hideFooter={hideFooter}
        initialState={initialStateProps}
        isCellEditable={handleIsCellEditable}
        isRowSelectable={isRowSelectable}
        key={contextName || 'data-grid-instance'}
        loading={loading}
        nonce={getNonce()}
        onColumnVisibilityModelChange={(model) => {
          handleStateContextChange(model, 'columns');
          onColumnVisibilityModelChange && onColumnVisibilityModelChange(model);
        }}
        onFilterModelChange={(model, details) => {
          onRowSelectionModelChange &&
            onRowSelectionModelChange({ ids: new Set(), type: 'include' });
          handleStateContextChange(model, 'filter');
          onFilterModelChange && onFilterModelChange(model, details);
        }}
        onPaginationModelChange={onPaginationModelChange}
        onRowClick={onRowClick}
        onRowDoubleClick={(e) => {
          isRowNavigation &&
            (isRowSelectable?.(e) ?? true) &&
            handleRowNavigation(e);
        }}
        onRowEditStop={handleRowEditStop}
        onRowModesModelChange={
          hasInRowEditConfig ? handleInRowModesModelChange : undefined
        }
        onRowSelectionModelChange={onRowSelectionModelChange}
        onRowOrderChange={onRowOrderChange}
        onSortModelChange={(model, details) => {
          onRowSelectionModelChange &&
            onRowSelectionModelChange({ ids: new Set(), type: 'include' });
          handleStateContextChange(model, 'sort');
          onSortModelChange && onSortModelChange(model, details);
        }}
        pageSizeOptions={[25, 50, 100, 200]}
        pagination
        paginationMode={paginationMode}
        paginationModel={paginationModel}
        processRowUpdate={actionsConfig?.inRowEditConfig?.processRowUpdate}
        rowCount={resolvedRowCount}
        rowModesModel={hasInRowEditConfig ? internalRowModesModel : undefined}
        rowReordering={rowReordering}
        rowSelectionModel={rowSelectionModel}
        rows={dataFetchError ? [] : rows}
        showToolbar={showToolbar}
        slots={{
          toolbar: CustomToolbar,
          noRowsOverlay: CustomNoRowsOverlay as JSXElementConstructor<
            HTMLAttributes<HTMLDivElement>
          >,
          row: CustomDataGridRow,
          ...slots,
        }}
        sortModel={sortModel ?? defaultSort}
        sortingMode={sortingMode}
        slotProps={{
          ...slotProps,
          row: {
            ...(slotProps?.row ?? {}),
            onRowContextMenu: handleRowContextMenu,
          } as GridSlotsComponentsProps['row'],
          noRowsOverlay: {
            dataFetchError,
            noDataDisplay,
          } as HTMLAttributes<HTMLDivElement>,
          toolbar: toolbarSlotProps as CustomToolbarProps,
        }}
        sx={sx}
        treeData={treeData}
      />
      <Menu
        open={!!rowContextMenuConfig}
        onClose={() => setRowContextMenuConfig(undefined)}
        anchorReference="anchorPosition"
        anchorPosition={
          rowContextMenuConfig
            ? {
                top: rowContextMenuConfig.mouseY,
                left: rowContextMenuConfig.mouseX,
              }
            : undefined
        }
      >
        <MenuItem onClick={handleOpenRowInNewPage}>Open in new tab</MenuItem>
      </Menu>
      {actionsConfig?.changeActiveConfig && (
        <ConfirmChangeActiveDialog
          showDialog={!!changeActiveStatusConfig?.id}
          isActive={changeActiveStatusConfig?.isActive}
          itemName={actionsConfig.changeActiveConfig.targetStr || 'row'}
          onClose={() => setChangeActiveStatusConfig(undefined)}
          onConfirm={changeActiveStatus}
          isLoading={isChangingActiveStatus}
        />
      )}
      {actionsConfig?.deleteConfig && (
        <ConfirmDeleteDialog
          showDialog={!!rowIdToDelete}
          itemName={actionsConfig.deleteConfig.targetStr}
          message={deleteMessage}
          onClose={() => setRowIdToDelete('')}
          onConfirm={deleteRow}
          isLoading={isDeleting}
        />
      )}
    </Box>
  );
}
